import React, { useState, useContext, useEffect } from 'react';
import { Card, Button, Icon, Popup } from 'semantic-ui-react';
import { ContactContext } from '../../contexts/contactContext';
import { AppContext } from '../../contexts/appContext';

type RecordingState = 'recording' | 'stopped';

const cleanupOldRecordingCache = () => {
  const MAX_AGE_MS = 3 * 60 * 60 * 1000; // 3 hours
  const now = Date.now();
  
  Object.keys(localStorage).forEach(key => {
    if (key.startsWith('recording_')) {
      try {
        const data = JSON.parse(localStorage.getItem(key) || '{}');
        if (data.timestamp && (now - data.timestamp > MAX_AGE_MS)) {
          localStorage.removeItem(key);
        }
      } catch (parseError) {
        // If it's not JSON (old format), remove it
        localStorage.removeItem(key);
      }
    }
  });
};

export const RecordingControls = () => {
  const [recordingState, setRecordingState] = useState<RecordingState>('recording');
  const [loading, setLoading] = useState(false);
  const { selectedContact } = useContext(ContactContext);
  const { config } = useContext(AppContext);

  // Cleanup old cache entries on mount
  useEffect(() => {
    cleanupOldRecordingCache();
  }, []);

  // Reset state when contact changes
  useEffect(() => {
    if (selectedContact) {
      const contactId = selectedContact.getContactId();
      try {
        const cachedData = localStorage.getItem(`recording_${contactId}`);
        if (cachedData) {
          const data = JSON.parse(cachedData);
          if (data.state === 'stopped') {
            setRecordingState('stopped');
          } else {
            setRecordingState('recording');
          }
        } else {
          setRecordingState('recording');
        }
      } catch (cacheReadError) {
        console.warn('Failed to read cached recording state:', cacheReadError);
        setRecordingState('recording');
      }
    }
  }, [selectedContact]);

  const getContactIds = () => {
    if (!selectedContact) return {};
    const contactId = selectedContact.getContactId();
    const initialContactId = selectedContact.getOriginalContactId ? selectedContact.getOriginalContactId() : selectedContact.getContactId();
    return { contactId, initialContactId };
  };

  const apiBase = config?.api?.baseURL?.replace(/\/$/, '') || '';

  const handleStop = async () => {
    setLoading(true);
    const { contactId, initialContactId } = getContactIds();
    console.log('Stopping recording:', { contactId, initialContactId });
    try {
      const response = await fetch(`${apiBase}/stop-recording`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "x-api-key": config?.api?.apiKey || ''
        },
        body: JSON.stringify({ contactId, initialContactId })
      });
      if (response.ok) {
        const result = await response.json();
        console.log('Recording stopped successfully:', result);
        setRecordingState('stopped');
        localStorage.setItem(`recording_${contactId}`, JSON.stringify({
          state: 'stopped',
          timestamp: Date.now()
        }));
      } else {
        const errorText = await response.text();
        let errorMessage = errorText;
        
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorText;
        } catch (jsonParseError) {
        }
        
        // Check if recording is already stopped
        if (errorMessage.includes('not allowed in current voice recording state')) {
          console.log('Recording already stopped, updating UI state');
          setRecordingState('stopped');
          localStorage.setItem(`recording_${contactId}`, JSON.stringify({
            state: 'stopped',
            timestamp: Date.now()
          }));
        } else {
          console.error('Failed to stop recording:', {
            status: response.status,
            statusText: response.statusText,
            error: errorMessage,
            requestData: { contactId, initialContactId }
          });
          alert(`Failed to stop recording: ${errorMessage}`);
        }
      }
    } catch (networkError) {
      console.error('Error stopping recording:', networkError);
      alert(`Error stopping recording: ${networkError}`);
    }
    setLoading(false);
  };

  return (
    <Card className="card-jmb" style={{marginTop: '10px'}} fluid={true}>
      <Card.Content className="header ean">
        <Card.Header textAlign="left" style={{ fontSize: '0.9em' }}>
          <Icon name="record" />
          Recording Controls
        </Card.Header>
      </Card.Content>
      <Card.Content>
        <div style={{ 
          display: 'flex', 
          gap: 12,
          padding: '4px 0',
          alignItems: 'center'
        }}>
          {/* Status indicator */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: recordingState === 'recording' ? '#e74c3c' : '#95a5a6',
              animation: recordingState === 'recording' ? 'pulse 2s infinite' : 'none',
              boxShadow: recordingState === 'recording' ? '0 0 6px rgba(231, 76, 60, 0.6)' : 'none'
            }} />
            <span style={{ 
              fontSize: '14px', 
              fontWeight: 600,
              color: recordingState === 'recording' ? '#e74c3c' : '#34495e'
            }}>
              {recordingState === 'recording' ? 'Recording' : 'Stopped'}
            </span>
          </div>

          {/* Stop button */}
          {recordingState === 'recording' && (
            <Popup
              content="Once stopped, recording cannot be restarted."
              trigger={
                <Button
                  color="red"
                  onClick={handleStop}
                  disabled={loading}
                  icon
                  labelPosition="left"
                  size="small"
                  compact
                  style={{ borderRadius: '8px', minWidth: '90px' }}
                >
                  <Icon name="stop" /> Stop
                </Button>
              }
            />
          )}
        </div>

        {/* Add CSS animation */}
        <style>{`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
              transform: scale(1);
            }
            50% {
              opacity: 0.6;
              transform: scale(1.1);
            }
          }
        `}</style>
      </Card.Content>
    </Card>
  );
};